import type { NavigateFunction } from "react-router-dom";
import {
  CAMPAIGN_BUILDER_STEPS,
  type CampaignBuilderStepNumber,
  releaseCampaignBuilderDraft,
} from "@/constants/campaignBuilder";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import type { Campaign } from "@/interfaces/campaing";
import type { CampaignStatus } from "@/types/types";
import { formatDateLong } from "@/utils/date-format";
import { formatUsd } from "@/utils/number-format";

/**
 * "Nov 13, 2026". A date-only "YYYY-MM-DD" (the API's campaign-zone start day) is that day in every
 * browser zone; anything with a time part renders in the local zone. Both handled by the shared
 * `formatDateLong` (utils/date-format) — this name stays for the campaign components that import it.
 */
export function formatDate(dateStr: string): string {
  return formatDateLong(dateStr);
}

// Every status carries an explicit verb (the test enforces full coverage). The deliberate-act
// overlays (CAMPAIGN_RULES v1.3): paused/archived read "Started" (the campaign had launched before
// the act); canceled/deleted read "Created" (they never ran — like a draft).
const CAMPAIGN_DATE_VERBS: Record<CampaignStatus, string> = {
  [CAMPAIGN_STATUSES.DRAFT]: "Created",
  [CAMPAIGN_STATUSES.SCHEDULED]: "Starts",
  [CAMPAIGN_STATUSES.ACTIVE]: "Started",
  [CAMPAIGN_STATUSES.PAUSED]: "Started",
  [CAMPAIGN_STATUSES.CLOSED]: "Started",
  [CAMPAIGN_STATUSES.CANCELED]: "Created",
  [CAMPAIGN_STATUSES.ARCHIVED]: "Started",
  [CAMPAIGN_STATUSES.DELETED]: "Created",
  // Disabled is an active campaign with its link turned off — it had launched, so "Started".
  [CAMPAIGN_STATUSES.DISABLED]: "Started",
};

/**
 * The API (`deriveCampaignDateRange`) chooses which date `startDate` holds per status; this chooses
 * the verb that goes with it (GFG-382).
 */
export function formatCampaignDateLabel(campaign: Pick<Campaign, "status" | "startDate">): string {
  const verb = CAMPAIGN_DATE_VERBS[campaign.status] ?? "Started";
  return `${verb} ${formatDate(campaign.startDate)}`;
}

/** The names GFG-405 gives each way a campaign reaches its recipients. */
export const CAMPAIGN_DISTRIBUTION_LABELS = {
  gfg: "Standard Send",
  multiDate: "Automated Send",
  manual: "Manual Distribution",
  linkToList: "Manual Distribution (tracked)",
  universal: "Universal Link",
} as const;

/**
 * The legend beside the recipient count on a campaign card ("28 recipients · Standard Send"), so
 * the client knows which manage action the card offers: the distribution mode, refined by the
 * state the API derives for it (multi-date for a GfG send, a recipient list for manual links).
 */
export function formatCampaignDistribution({
  distribution,
}: Pick<Campaign, "distribution">): string {
  if (distribution.mode === "universal") return CAMPAIGN_DISTRIBUTION_LABELS.universal;
  if (distribution.mode === "manual") {
    return distribution.linkToList
      ? CAMPAIGN_DISTRIBUTION_LABELS.linkToList
      : CAMPAIGN_DISTRIBUTION_LABELS.manual;
  }
  return distribution.multiDate
    ? CAMPAIGN_DISTRIBUTION_LABELS.multiDate
    : CAMPAIGN_DISTRIBUTION_LABELS.gfg;
}

export function formatBudget(amount: number): string {
  return formatUsd(amount);
}

/** Always two decimal places, e.g. "$1,234.56"; negatives render as "-$12.34". */
export function formatMoney(amount: number): string {
  return formatUsd(amount);
}

export function getStepPath(step: CampaignBuilderStepNumber): string {
  return CAMPAIGN_BUILDER_STEPS.find((s) => s.number === step)!.path;
}

export function getCurrentStep(pathname: string): CampaignBuilderStepNumber {
  const match = CAMPAIGN_BUILDER_STEPS.slice()
    .reverse()
    .find((s) => pathname.endsWith(s.path.split("/").at(-1)!));
  return match?.number ?? 1;
}

/**
 * Open a NEW campaign in the builder at Step 1 with a budget tier (and optionally a country)
 * pre-selected. Releases any stashed EDIT id (so the builder starts fresh, not on the last-edited
 * campaign), then routes to the tier pre-select entry — budget as a route param, country as an
 * optional `?country=` query. Both ride the URL (self-cleaning; nothing to reset the way a
 * sessionStorage flag would). Shared by every Collections "send" entry point.
 *
 * The caller is responsible for the international gate: this only ever receives a non-US country
 * when the account may ship internationally, so the seeded country is always valid for the plan.
 */
export function openCampaignBuilderWithBudget(
  budget: number,
  navigate: NavigateFunction,
  country?: string,
): void {
  releaseCampaignBuilderDraft();
  const suffix = country && country !== "US" ? `?country=${encodeURIComponent(country)}` : "";
  navigate(`/campaign/create/tier/${budget}${suffix}`);
}
